import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import '../index.css';

function UserManagementPage() {
    const { hasRole } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [newUser, setNewUser] = useState({
        email: '',
        password: '',
        name: '',
        role: 'normal-user'
    });

    const [editUserData, setEditUserData] = useState({
        email: '',
        name: '',
        role: '',
        password: ''
    });

    useEffect(() => {
        if (hasRole('admin')) {
            loadUsers();
        }
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get('http://localhost:5000/api/users');
            setUsers(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load users');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await axios.post('http://localhost:5000/api/users', newUser);
            setNewUser({ email: '', password: '', name: '', role: 'normal-user' });
            setShowCreateForm(false);
            loadUsers();
            alert('User created successfully!');
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create user');
        }
    };

    const handleEditUser = (user) => {
        setEditingUser(user.id);
        setEditUserData({
            email: user.email,
            name: user.name,
            role: user.role,
            password: ''
        });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            const dataToUpdate = { ...editUserData };
            if (!dataToUpdate.password) delete dataToUpdate.password;

            await axios.put(`http://localhost:5000/api/users/${editingUser}`, dataToUpdate);
            setEditingUser(null);
            loadUsers();
            alert('User updated successfully!');
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update user');
        }
    };

    const handleToggleStatus = async (userId, currentStatus) => {
        try {
            await axios.put(`http://localhost:5000/api/users/${userId}/status`, { isActive: !currentStatus });
            loadUsers();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update status');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            await axios.delete(`http://localhost:5000/api/users/${userId}`);
            loadUsers();
            alert('User deleted successfully!');
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete user');
        }
    };

    if (!hasRole('admin')) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <h2>Access Denied</h2>
                <p>Only administrators can access this page.</p>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 className="page-title">👥 User Management</h1>
                <button
                    onClick={() => {
                        setShowCreateForm(!showCreateForm);
                        setEditingUser(null);
                    }}
                    className="btn-primary"
                    style={{ padding: '0.75rem 1.5rem' }}
                >
                    {showCreateForm ? '✕ Cancel' : '+ Create New User'}
                </button>
            </div>

            {error && <div className="card"><div className="error-message">{error}</div></div>}

            {showCreateForm && (
                <div className="card" style={{ marginBottom: '2rem' }}>
                    <h2 style={{ marginBottom: '1.5rem' }}>Create New User</h2>
                    <form onSubmit={handleCreateUser}>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Email</label>
                                <input
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Name</label>
                                <input
                                    type="text"
                                    value={newUser.name}
                                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Password</label>
                                <input
                                    type="password"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                    required
                                    minLength="6"
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Role</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                    required
                                >
                                    <option value="normal-user">Normal User</option>
                                    <option value="invoice-editor">Sales Order Editor</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                        </div>
                        <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }}>
                            Create User
                        </button>
                    </form>
                </div>
            )}

            {editingUser && (
                <div className="card" style={{ marginBottom: '2rem', border: '2px solid #3b82f6' }}>
                    <h2 style={{ marginBottom: '1.5rem' }}>Edit User</h2>
                    <form onSubmit={handleUpdateUser}>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Email</label>
                                <input
                                    type="email"
                                    value={editUserData.email}
                                    onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Name</label>
                                <input
                                    type="text"
                                    value={editUserData.name}
                                    onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>New Password (leave blank to keep current)</label>
                                <input
                                    type="password"
                                    value={editUserData.password}
                                    onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })}
                                    minLength="6"
                                    placeholder="Reset password..."
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Role</label>
                                <select
                                    value={editUserData.role}
                                    onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                                    required
                                >
                                    <option value="normal-user">Normal User</option>
                                    <option value="invoice-editor">Sales Order Editor</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <button type="submit" className="btn-primary">
                                Update User
                            </button>
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => setEditingUser(null)}
                                style={{
                                    background: '#ef4444',
                                    color: 'white'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="card">
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>Loading users...</div>
                ) : (
                    <div className="table-container">
                        <table className="product-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td>
                                            <span style={{
                                                textTransform: 'capitalize',
                                                padding: '0.25rem 0.5rem',
                                                background: '#f3f4f6',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem'
                                            }}>
                                                {user.role.replace('-', ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                background: user.isActive ? '#dcfce7' : '#fee2e2',
                                                color: user.isActive ? '#15803d' : '#991b1b'
                                            }}>
                                                {user.isActive ? '✓ Active' : '✕ Inactive'}
                                            </span>
                                        </td>
                                        <td>{user.createdAtIST || new Date(user.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    title="Edit User"
                                                    style={{
                                                        padding: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: '#3b82f6',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.2s, background 0.2s'
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleToggleStatus(user.id, user.isActive)}
                                                    title={user.isActive ? 'Deactivate User' : 'Activate User'}
                                                    style={{
                                                        padding: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: user.isActive ? '#fbbf24' : '#10b981',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.2s, background 0.2s'
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    {user.isActive ? (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="1" y="5" width="22" height="14" rx="7" ry="7"></rect>
                                                            <circle cx="16" cy="12" r="3"></circle>
                                                        </svg>
                                                    ) : (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="1" y="5" width="22" height="14" rx="7" ry="7"></rect>
                                                            <circle cx="8" cy="12" r="3"></circle>
                                                        </svg>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteUser(user.id)}
                                                    title="Delete User"
                                                    style={{
                                                        padding: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.2s, background 0.2s'
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default UserManagementPage;
